// https://www.knowledgehub.org.za/system/files/elibdownloads/2019-07/Section%25202%2520Clinical%2520Resource%2520Guide.pdf
import {
  ClinicalImpressionAssessmentValueCode,
  ClinicalImpressionPlanValueCode,
} from '@quromedical/fhir-common';
import { MeasurementName } from '@quromedical/models';
import { titles } from 'core/vitals';
import { NestedComment } from 'design-system';
import { strings } from 'strings';

import { Options } from './NestedNoteForm';

const dayTimeOptions: NestedComment.Comment[] = [
  {
    id: 'morning',
    type: 'comment',
    display: 'Morning',
  },
  {
    id: 'afternoon',
    type: 'comment',
    display: 'Afternoon',
  },
  {
    id: 'evening',
    type: 'comment',
    display: 'Evening',
  },
];

const locationOptions: NestedComment.Comment[] = [
  {
    id: 'virtual',
    type: 'comment',
    display: 'Virtual',
  },
  {
    id: 'in-person',
    type: 'comment',
    display: 'In-person',
  },
];

const painOPQRS: NestedComment.Comment[] = [
  {
    id: 'onset',
    display: 'Onset',
    type: 'comment',
    textRequired: true,
  },
  {
    id: 'provoking',
    display: 'Provoking',
    type: 'comment',
    textRequired: true,
    color: 'status-warning',
  },
  {
    id: 'palliating',
    display: 'Palliating',
    type: 'comment',
    textRequired: true,
    color: 'status-success',
  },
  {
    id: 'quality',
    display: 'Quality',
    type: 'comment',
    textRequired: true,
  },
  {
    id: 'radiating',
    display: 'Radiating',
    type: 'comment',
    textRequired: true,
  },
  {
    id: 'severity',
    display: 'Severity',
    type: 'comment',
    textRequired: true,
  },
];

const cardiovascularSymptoms: NestedComment.Node[] = [
  {
    id: 'chest-pain',
    display: 'Chest Pain',
    type: 'group',
    children: painOPQRS,
  },
  {
    id: 'palpitations',
    display: 'Palpitations',
    type: 'comment',
  },
  {
    id: 'shortness-of-breath',
    display: 'Shortness of Breath',
    type: 'comment',
    textRequired: true,
  },
];

const bodySystems: NestedComment.Group[] = [
  {
    id: 'cardiovascular',
    display: 'Cardiovascular',
    type: 'group',
    children: cardiovascularSymptoms,
  },
  {
    id: 'respiratory',
    display: 'Respiratory',
    type: 'group',
    children: [],
  },
];

const other: NestedComment.Comment = {
  id: 'other',
  type: 'comment',
  display: 'Other',
  textRequired: true,
};

const objectiveMeasures: MeasurementName[] = [
  MeasurementName.HeartRate,
  MeasurementName.RespiratoryRate,
  MeasurementName.CoreTemperature,
  MeasurementName.SkinTemperature,
  MeasurementName.OxygenSaturation,
  MeasurementName.PerfusionIndex,
  MeasurementName.Glucose,
  MeasurementName.CombinedArterialBloodPressure,
  MeasurementName.Weight,
];

/**
 * should see day of visit in the note with etc.
 * clinical operations should be first
 * subjective
 * using body systems
 * - most common for each system, and then have other
 * - OPQRS concept for certain symptoms
 *
 * - under objective we need test results and stuff
 * - things that are in templates that should be here
 * - add measurement types for vitals?
 * - show vitals on note like before
 *
 * Show day/plan on note so clinical team doesn't need to deal with it
 * - can clinical team customize the day/plan for patient's note?
 * - show intensity level on note
 *
 * NOT to do with notes
 * - Notification that a patient needs a discharge/auto summary
 * - Admissions as default patient view
 * - maybe ability to pause an admission and swap to a different service before continuing
 * - things like bloods/oxygen/etc. need to be done part of the HaH plan and not TCP for example
 * - needs discussion (complexities around claims etc.)
 *
 * Different types of notes
 * - Onboarding
 * - Clinical callout
 * - Home visit
 * - MIA Note - can't make a SOAP note
 * - Clinical updates (e.g. following up on something)
 *
 * Can we do in some way note corrections?
 */
const investigationTypes: NestedComment.Node[] = [
  {
    id: 'bloods',
    display: 'Bloods',
    children: [
      {
        id: 'fbc',
        display: 'FBC',
        type: 'group',
        children: [
          {
            id: 'normal',
            display: 'Normal',
            type: 'comment',
          },
          {
            id: 'very-low-hb',
            display: 'Very Low HB',
            type: 'comment',
            color: 'status-critical',
          },
          {
            id: 'low-hb',
            display: 'Low HB',
            type: 'comment',
            color: 'status-warning',
          },
          {
            id: 'high-white-cell-count',
            display: 'Hight WCC',
            type: 'comment',
            color: 'status-warning',
          },
        ],
      },
      {
        id: 'u-and-e',
        display: 'U&E',
        type: 'comment',
        textRequired: true,
      },
    ],
    type: 'group',
  },
  {
    id: 'cultures',
    display: 'Cultures',
    children: [],
    type: 'group',
  },
  {
    id: 'radiology',
    display: 'Radiology',
    children: [],
    type: 'group',
  },
];

export const baseNoteOptions: Options = {
  clinicalOperations: {
    type: 'group',
    display: 'Clinical Operations',
    id: 'clinical-operations',
    icon: 'text-c',
    children: [
      ...locationOptions,
      ...dayTimeOptions,
      {
        id: 'other',
        type: 'comment',
        display: 'Other',
        textRequired: true,
      },
    ],
  },
  subjective: {
    id: 'subjective',
    type: 'group',
    display: 'Subjective',
    icon: 'text-s',
    children: [
      {
        id: 'unwell',
        type: 'group',
        display: 'Unwell',
        color: 'status-warning',
        children: bodySystems,
      },
      {
        id: 'ok',
        type: 'comment',
        display: 'Nil issues reported',
        color: 'status-success',
      },
      {
        id: 'other',
        type: 'comment',
        display: 'Other',
        textRequired: true,
      },
    ],
  },
  assessment: {
    id: 'assessment',
    display: 'Assessment',
    type: 'group',
    icon: 'text-a',
    children: [
      {
        type: 'comment',
        display: 'Stable',
        id: ClinicalImpressionAssessmentValueCode.Stable,
      },
      {
        type: 'comment',
        display: 'Improving',
        color: 'status-success',
        id: ClinicalImpressionAssessmentValueCode.Improving,
      },
      {
        type: 'group',
        display: 'Deteriorating',
        color: 'status-warning',
        id: ClinicalImpressionAssessmentValueCode.Deteriorating,
        children: [
          {
            id: 'critical',
            type: 'group',
            display: 'Critical',
            color: 'status-critical',
            children: [
              {
                type: 'comment',
                id: 'unresponsive',
                display: 'Unresponsive',
                color: 'status-critical',
              },
              {
                type: 'comment',
                id: 'heart-stopped',
                display: 'Heart stopped',
                color: 'status-critical',
              },
              {
                type: 'comment',
                id: 'not-breathing',
                display: 'Not Breathing',
                color: 'status-critical',
              },
              {
                type: 'comment',
                id: 'other',
                display: 'Other',
                color: 'status-critical',
                textRequired: true,
              },
            ],
          },
          {
            type: 'comment',
            id: 'other',
            display: 'Other',
            color: 'status-warning',
            textRequired: true,
          },
        ],
      },
    ],
  },
  objective: {
    id: 'objective',
    type: 'group',
    display: 'Objective',
    icon: 'text-o',
    children: [
      {
        id: 'physical',
        display: 'Physical',
        type: 'group',
        children: [
          {
            id: 'location',
            display: 'Location',
            type: 'group',
            children: [
              {
                id: 'bed',
                display: 'Bed',
                type: 'comment',
              },
              {
                id: 'lounge',
                display: 'Lounge',
                type: 'comment',
              },
              {
                id: 'outside',
                display: 'Outside',
                type: 'comment',
              },
            ],
          },
          {
            id: 'posture',
            display: 'Posture',
            type: 'group',
            children: [
              {
                id: 'laying',
                display: 'Laying',
                type: 'comment',
              },
              {
                id: 'sitting',
                display: 'Sitting',
                type: 'comment',
              },
              {
                id: 'standing',
                display: 'Standing',
                type: 'comment',
              },
            ],
          },
          {
            id: 'jaccold',
            display: 'JACCOLD',
            type: 'group',
            children: [
              {
                id: 'jaundice',
                display: 'Jaundice',
                type: 'group',
                children: [
                  {
                    id: 'conjuctival',
                    display: 'Conjuctival',
                    type: 'comment',
                  },
                  {
                    id: 'skin',
                    display: 'Skin',
                    type: 'comment',
                  },
                ],
              },
              {
                id: 'anemia',
                display: 'Anemia',
                type: 'group',
                children: [
                  {
                    id: 'conjuctival-pallor',
                    display: 'Conjuctival Pallor',
                    type: 'comment',
                  },
                  {
                    id: 'skin-pallor',
                    display: 'Skin Pallor',
                    type: 'comment',
                  },
                ],
              },
              {
                id: 'cyanosis',
                display: 'Cyanosis',
                type: 'group',
                children: [
                  {
                    id: 'central',
                    display: 'Central',
                    type: 'comment',
                  },
                  {
                    id: 'peripheral',
                    display: 'Peripheral',
                    type: 'comment',
                  },
                ],
              },
              {
                id: 'clubbing',
                display: 'Clubbing',
                type: 'comment',
              },
              {
                id: 'oedema',
                display: 'Oedema',
                type: 'group',
                children: [
                  {
                    id: 'pedal',
                    display: 'Pedal',
                    type: 'comment',
                  },
                  {
                    id: 'arms',
                    display: 'Arms',
                    type: 'comment',
                  },
                  {
                    id: 'anasarca',
                    display: 'Anasarca',
                    type: 'comment',
                  },
                ],
              },
              {
                id: 'lymph-adenopathy',
                display: 'Lymph Adenopathy',
                type: 'group',
                children: [
                  {
                    id: 'inguinal',
                    display: 'Inguinal',
                    type: 'comment',
                  },
                  {
                    id: 'neck',
                    display: 'Neck',
                    type: 'comment',
                  },
                  {
                    id: 'axillary',
                    display: 'Axillary',
                    type: 'comment',
                  },
                  {
                    id: 'supraclavicular',
                    display: 'Supraclavicular',
                    type: 'comment',
                  },
                ],
              },
              {
                id: 'dehydration',
                display: 'Dehydration',
                type: 'group',
                children: [
                  {
                    id: 'moderate',
                    display: 'Moderate',
                    type: 'comment',
                  },
                  {
                    id: 'severe',
                    display: 'Severe',
                    type: 'comment',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'results',
        display: 'Results',
        type: 'group',
        children: investigationTypes,
      },
      {
        id: 'vitals',
        display: 'Vitals',
        type: 'group',
        children: [
          ...Object.values(objectiveMeasures).map(
            key =>
              ({
                id: key,
                type: 'comment',
                display: titles[key as MeasurementName] || key,
                textRequired: true,
              }) as const
          ),
          other,
        ],
      },
    ],
  },
  plan: {
    id: 'plan',
    type: 'group',
    display: 'Plan',
    icon: 'text-p',
    children: [
      {
        type: 'comment',
        display: 'Continue',
        id: ClinicalImpressionPlanValueCode.Continue,
      },
      {
        type: 'group',
        display: 'Amend',
        id: ClinicalImpressionPlanValueCode.Amend,
        children: [
          {
            id: 'investigations',
            display: 'Investigations',
            type: 'group',
            children: investigationTypes,
          },
          other,
        ],
      },
      {
        type: 'comment',
        display: strings.SOAPNotePlanDischarge,
        id: ClinicalImpressionPlanValueCode.Discharge,
        textRequired: true,
      },
      {
        type: 'comment',
        display: strings.SOAPNotePlanEscalate,
        id: ClinicalImpressionPlanValueCode.Escalate,
        textRequired: true,
      },
      {
        type: 'comment',
        display: strings.SOAPNotePlanUpgradedToHAH,
        id: ClinicalImpressionPlanValueCode.UpgradedToHAH,
        textRequired: true,
      },
    ],
  },
};
